import React, { useEffect, useState, useMemo, useRef } from 'react';
import { GoogleMap, Marker, InfoWindow, useLoadScript, MarkerClustererF } from '@react-google-maps/api';
import env from '../../../env';
import { Button } from '../../../components/Buttons';
import CheckboxComponent from '../../../containers/App/Components/Checkbox/Checkbox';
import { VehicleStatusLabel } from '../../Vehicles/components/VehicleStatusLabel';
import { RegplateLabel } from '../../Vehicles/components/RegplateLabel';
import { getDate, getVehicleTaskOpen } from '../../Vehicles/helpers';
import { TRANSPORT_ORDER_STATUS } from '../constants';
import { OnMouseOverNotification } from '../../Vehicles/components/OnMouseOverNotification';

const containerStyle = {
  width: '100%',
  height: '800px',
};

const center = {
  lat: 52.5377778,
  lng: 6.1665918,
};

function MapWithMarkers({ weblogin, isSelectedTransportOrder, onSelectTransportOrder, transportOrders }) {
  const [markerData, setMarkerData] = useState([]);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [gestureHandling, setGestureHandling] = useState('none');
  const mapRef = useRef(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: env.google_maps_api_key,
  });

  const transportOrdersToShow = Array.isArray(transportOrders) ? transportOrders : [];

  const addressesIncoming = useMemo(
    () =>
      transportOrdersToShow
        .filter(order => order.status !== TRANSPORT_ORDER_STATUS.Canceled) // Exclude cancelled orders
        .map(order => {
          const addressFrom = order?.addressFrom;
          const direction = order?.type;
          const vehicle = order?.vehicle;
          if (addressFrom?.addressLine1 && addressFrom?.postcode && addressFrom?.city && direction === 'incoming') {
            return {
              address: `${addressFrom.addressLine1}, ${addressFrom.postcode}, ${addressFrom.city}`,
              lat: +addressFrom?.lat,
              lng: +addressFrom?.lng,
              vehicle: vehicle,
              transportOrder: order,
            };
          }
          return null;
        })
        .filter(Boolean),
    [transportOrdersToShow],
  );

  const addressesOutgoing = useMemo(
    () =>
      transportOrdersToShow
        .filter(order => order.status !== TRANSPORT_ORDER_STATUS.Canceled) // Exclude cancelled orders
        .map(order => {
          const addressTo = order?.addressTo;
          const direction = order?.type;
          const vehicle = order?.vehicle;
          if (addressTo?.addressLine1 && addressTo?.postcode && addressTo?.city && direction === 'outgoing') {
            return {
              address: `${addressTo.addressLine1}, ${addressTo.postcode}, ${addressTo.city}`,
              lat: +addressTo?.lat,
              lng: +addressTo?.lng,
              vehicle: vehicle,
              transportOrder: order,
            };
          }
          return null;
        })
        .filter(Boolean),
    [transportOrdersToShow],
  );

  const addressesOther = useMemo(
    () =>
      transportOrdersToShow
        .filter(order => order.status !== TRANSPORT_ORDER_STATUS.Canceled) // Exclude cancelled orders
        .map(order => {
          const addressFrom = order?.addressFrom;
          const direction = order?.type;
          const vehicle = order?.vehicle;
          if (addressFrom?.addressLine1 && addressFrom?.postcode && addressFrom?.city && direction === 'other') {
            return {
              address: `${addressFrom.addressLine1}, ${addressFrom.postcode}, ${addressFrom.city}`,
              lat: +addressFrom?.lat,
              lng: +addressFrom?.lng,
              vehicle: vehicle,
              transportOrder: order,
            };
          }
          return null;
        })
        .filter(Boolean),
    [transportOrdersToShow],
  );

  const enableInteraction = () => {
    setGestureHandling('auto');
  };

  const disableInteraction = event => {
    if (mapRef.current && !mapRef.current.contains(event.target)) {
      setGestureHandling('none');
    }
  };

  const renderRemarkText = (remarks, transportOrderId) => {
    if (!remarks?.length) {
      return (
        <img
          style={{ cursor: 'pointer', opacity: '0.15', marginRight: '5px' }}
          src="https://vindat.indewal.nl/storage/documents/ca10dd49-aac5-4351-a014-a3cfeee9f88d.png"
          title=""
          width="18"
        />
      );
    }
    return null;
  };

  const renderOpennedTaskAlert = vehicle => {
    if (getVehicleTaskOpen(vehicle)?.length) {
      return (
        <OnMouseOverNotification
          message={
            <div className="d-flex flex-column">
              <span className="red-text">{'Kanban: openstaande taak'}</span>
              <span>{getVehicleTaskOpen(vehicle)[0]}</span>
            </div>
          }
          minWidth={'250px'}
          left={'0px'}
        />
      );
    }
    return null;
  };

  useEffect(() => {
    const geocodeAddresses = async () => {
      const geocodedPositions = [];
      const geocoder = new window.google.maps.Geocoder();
      //when there is no geocodedPosition then geocode it and save it to the address with longitude and latitude
      for (const addressObj of addressesOutgoing.concat(addressesIncoming, addressesOther)) {
        if (addressObj?.lat && addressObj?.lng) {
          geocodedPositions.push({
            lat: addressObj?.lat,
            lng: addressObj?.lng,
            address: addressObj.address,
            vehicle: addressObj.vehicle,
            transportOrder: addressObj.transportOrder,
          });
        } else {
          await new Promise(resolve => {
            geocoder.geocode({ address: addressObj.address }, (results, status) => {
              console.log('Google request Google request Google request ');
              if (status === 'OK' && results[0]) {
                geocodedPositions.push({
                  lat: results[0].geometry.location.lat(),
                  lng: results[0].geometry.location.lng(),
                  address: addressObj.address,
                  vehicle: addressObj.vehicle,
                  transportOrder: addressObj.transportOrder,
                });
              } else {
                console.error('Geocode failed: ', status);
              }
              resolve();
            });
          });
        }
      }

      setMarkerData(geocodedPositions);
    };

    if (isLoaded) {
      geocodeAddresses();
    }
  }, [addressesOutgoing, addressesIncoming, addressesOther, isLoaded]);

  useEffect(() => {
    document.addEventListener('click', disableInteraction);
    return () => document.removeEventListener('click', disableInteraction);
  }, []);

  if (!isLoaded) return <div>Loading...</div>;

  const handleMarkerClick = marker => {
    const associatedMarkers = markerData.filter(m => m.lat === marker.lat && m.lng === marker.lng);
    setSelectedMarker({
      position: { lat: marker.lat, lng: marker.lng },
      markers: associatedMarkers, // Store complete marker data
    });
  };

  const clustererOptions = {
    gridSize: 10,
    maxZoom: 15,
    averageCenter: true,
    minimumClusterSize: 4,
  };

  return (
    <div ref={mapRef} onClick={enableInteraction}>
      <GoogleMap mapContainerStyle={containerStyle} center={center} zoom={5} options={{ gestureHandling }}>
        <MarkerClustererF options={clustererOptions}>
          {clusterer =>
            markerData.map((marker, index) => {
              const isOutgoing = addressesOutgoing.some(a => a.address === marker.address);
              const isIncoming = addressesIncoming.some(a => a.address === marker.address);
              const isOther = addressesOther.some(a => a.address === marker.address);
              const iconUrl =
                isIncoming && isOutgoing
                  ? 'https://vindat.indewal.nl/public/images/greenRedMarker.png'
                  : isOutgoing
                  ? 'https://vindat.indewal.nl/public/images/redMarker.png'
                  : isOther
                  ? 'https://vindat.indewal.nl/public/images/BlueMarker.png'
                  : 'https://vindat.indewal.nl/public/images/greenMarker.png';

              return (
                <Marker
                  key={index}
                  position={{ lat: marker.lat, lng: marker.lng }}
                  clusterer={clusterer}
                  onClick={() => handleMarkerClick(marker)}
                  icon={{
                    url: iconUrl,
                    labelOrigin: new window.google.maps.Point(11, -10),
                  }}
                  label={{
                    text: markerData.filter(m => m.lat === marker.lat && m.lng === marker.lng).length.toString(),
                    color: 'black',
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                />
              );
            })
          }
        </MarkerClustererF>
        {selectedMarker && (
          <InfoWindow position={selectedMarker.position} onCloseClick={() => setSelectedMarker(null)}>
            <div className="container-fluid">
              {[...new Set(selectedMarker.markers.map(m => m.address))].map((address, index) => (
                <div key={index} className="mb-4">
                  <p>
                    <strong>
                      {selectedMarker.markers.find(m => m.address === address)?.transportOrder?.type === 'outgoing'
                        ? selectedMarker.markers.find(m => m.address === address)?.transportOrder?.companyTo?.name ||
                          'Onbekend'
                        : selectedMarker.markers.find(m => m.address === address)?.transportOrder?.companyFrom?.name ||
                          'Onbekend'}
                    </strong>
                  </p>
                  <p>
                    <strong>{address}</strong>
                  </p>
                </div>
              ))}

              <div className="row g-4">
                {/* Incoming Vehicles */}
                <div className="col-12">
                  {selectedMarker.markers.some(m =>
                    addressesIncoming.some(a => a.address === m.address && a.vehicle === m.vehicle),
                  ) && (
                    <>
                      <h5>Inkomend</h5>
                      <div className="list-group">
                        <div className="list-group-item bg-light d-flex justify-content-between fw-bold">
                          <span style={{ width: '40%' }}>Voertuig</span>
                          <span style={{ width: '20%' }}>Status</span>
                          <span style={{ width: '40%' }}>Adres</span>
                        </div>
                        {selectedMarker.markers
                          .filter(m => addressesIncoming.some(a => a.address === m.address && a.vehicle === m.vehicle))
                          .map((m, index) => (
                            <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                              <div style={{ width: '40%' }} className="d-flex align-items-center">
                                <CheckboxComponent
                                  disabled={weblogin}
                                  checked={isSelectedTransportOrder(m.transportOrder?.id)}
                                  onChange={e => onSelectTransportOrder(e, m.transportOrder)}
                                />
                                {renderRemarkText(m.transportOrder?.remarks, m.transportOrder?.id)}
                                {renderOpennedTaskAlert(m.vehicle)}
                                {m.transportOrder?.individual && (
                                  <svg width="12" height="12" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
                                    <title>Los transport</title>
                                    <circle cx="6" cy="6" r="6" fill="green" />
                                  </svg>
                                )}
                                <span className="ms-3">
                                  {m.transportOrder.vehicleManual ? (
                                    <>{m.transportOrder.vehicleManual || 'Geen titel'}</>
                                  ) : m.vehicle?.regplate ? (
                                    <>
                                      <RegplateLabel regplate={m.vehicle.regplate} width="80" />{' '}
                                      {m.vehicle?.brand_name || 'Unknown Brand'} {m.vehicle?.model_name || 'Unknown Model'}
                                    </>
                                  ) : (
                                    <>
                                      {m.vehicle?.vin?.slice(-6) || 'Unknown VIN'} {m.vehicle?.brand_name || 'Unknown Brand'}{' '}
                                      {m.vehicle?.model_name || 'Unknown Model'}
                                    </>
                                  )}
                                </span>
                              </div>
                              <div style={{ width: '20%' }}>
                                <span
                                  className={`badge ${m.vehicle?.orb_incoming_complete ? 'bg-success' : 'bg-danger'} me-2`}
                                >
                                  {m.vehicle?.orb_incoming_complete ? 'Vrijwaring' : 'Vrijwaring'}
                                </span>
                                <span
                                  className={`badge ${
                                    m.vehicle?.incoming_invoice_payment_status ? 'bg-success' : 'bg-danger'
                                  }`}
                                >
                                  {m.vehicle?.incoming_invoice_payment_status ? 'Betaling' : 'Betaling'}
                                </span>
                              </div>
                              <div
                                style={{
                                  width: '40%',
                                  color: m.transportOrder?.addressTo?.addressLine1 !== 'Kleefstraat 8' ? 'red' : 'inherit',
                                }}
                              >
                                {'naar: ' +
                                  m.transportOrder?.addressTo?.addressLine1 +
                                  ' ' +
                                  m.transportOrder?.addressTo?.postcode +
                                  ' ' +
                                  m.transportOrder?.addressTo?.city}
                                <br />
                                Beschikbaar vanaf:<strong>{getDate(m?.transportOrder?.dateToPlan)}</strong>
                              </div>
                            </div>
                          ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Outgoing Vehicles */}
                <div className="col-12">
                  {selectedMarker.markers.some(m =>
                    addressesOutgoing.some(a => a.address === m.address && a.vehicle === m.vehicle),
                  ) && (
                    <>
                      <h5>Uitgaand</h5>
                      <div className="list-group">
                        <div className="list-group-item bg-light d-flex justify-content-between fw-bold">
                          <span style={{ width: '40%' }}>Voertuig</span>
                          <span style={{ width: '20%' }}>Status</span>
                          <span style={{ width: '40%' }}>Adres</span>
                        </div>
                        {selectedMarker.markers
                          .filter(m => addressesOutgoing.some(a => a.address === m.address && a.vehicle === m.vehicle))
                          .map((m, index) => (
                            <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                              <div style={{ width: '40%' }} className="d-flex align-items-center">
                                <CheckboxComponent
                                  disabled={weblogin}
                                  checked={isSelectedTransportOrder(m.transportOrder?.id)}
                                  onChange={e => onSelectTransportOrder(e, m.transportOrder)}
                                />
                                {renderRemarkText(m.transportOrder?.remarks, m.transportOrder?.id)}
                                {renderOpennedTaskAlert(m.vehicle)}
                                {m.transportOrder?.individual && (
                                  <svg width="12" height="12" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
                                    <title>Los transport</title>
                                    <circle cx="6" cy="6" r="6" fill="green" />
                                  </svg>
                                )}
                                <span
                                  className="ms-3"
                                  title={
                                    'Van: ' +
                                    m.transportOrder?.addressFrom.addressLine1 +
                                    ' ' +
                                    m.transportOrder?.addressFrom.postcode +
                                    ' ' +
                                    m.transportOrder?.addressFrom.city
                                  }
                                >
                                  {m.transportOrder.vehicleManual ? (
                                    <>{m.transportOrder.vehicleManual || 'Geen titel'}</>
                                  ) : m.vehicle?.regplate ? (
                                    <>
                                      <RegplateLabel regplate={m.vehicle.regplate} width="80" />{' '}
                                      {m.vehicle?.brand_name || 'Unknown Brand'} {m.vehicle?.model_name || 'Unknown Model'}
                                    </>
                                  ) : (
                                    <>
                                      {m.vehicle?.vin?.slice(-6) || 'Unknown VIN'} {m.vehicle?.brand_name || 'Unknown Brand'}{' '}
                                      {m.vehicle?.model_name || 'Unknown Model'}
                                    </>
                                  )}
                                </span>
                              </div>
                              <div style={{ width: '20%' }}>
                                <span
                                  className={`badge ${
                                    m.vehicle?.outgoing_invoice_payment_status === 'PAID'
                                      ? 'bg-success'
                                      : m.vehicle?.outgoing_invoice_payment_status === 'PARTLY_PAID'
                                      ? 'bg-warning text-dark'
                                      : 'bg-danger'
                                  } me-2`}
                                >
                                  {m.vehicle?.outgoing_invoice_payment_status === 'PAID'
                                    ? 'Betaling'
                                    : m.vehicle?.outgoing_invoice_payment_status === 'PARTLY_PAID'
                                    ? 'Deels Betaald'
                                    : 'Betaling'}
                                </span>
                              </div>
                              <div
                                style={{
                                  width: '40%',
                                  color: m.transportOrder?.addressFrom?.addressLine1 !== 'Kleefstraat 8' ? 'red' : 'inherit',
                                }}
                              >
                                {'van: ' +
                                  m.transportOrder?.addressFrom?.addressLine1 +
                                  ' ' +
                                  m.transportOrder?.addressFrom?.postcode +
                                  ' ' +
                                  m.transportOrder?.addressFrom?.city}
                                <br />
                                Beschikbaar vanaf:<strong>{getDate(m?.transportOrder?.dateToPlan)}</strong>
                              </div>
                            </div>
                          ))}
                      </div>
                    </>
                  )}
                </div>
                {/* Other transports */}
                <div className="col-12">
                  {selectedMarker.markers.some(m =>
                    addressesOther.some(a => a.address === m.address && a.vehicle === m.vehicle),
                  ) && (
                    <>
                      <h5>Los transport</h5>
                      <div className="list-group">
                        <div className="list-group-item bg-light d-flex justify-content-between fw-bold">
                          <span style={{ width: '40%' }}>Voertuig</span>
                          <span style={{ width: '20%' }}>Status</span>
                          <span style={{ width: '40%' }}>Adres</span>
                        </div>
                        {selectedMarker.markers
                          .filter(m => addressesOther.some(a => a.address === m.address && a.vehicle === m.vehicle))
                          .map((m, index) => (
                            <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                              <div style={{ width: '40%' }} className="d-flex align-items-center">
                                <CheckboxComponent
                                  disabled={weblogin}
                                  checked={isSelectedTransportOrder(m.transportOrder?.id)}
                                  onChange={e => onSelectTransportOrder(e, m.transportOrder)}
                                />
                                {renderRemarkText(m.transportOrder?.remarks, m.transportOrder?.id)}
                                {renderOpennedTaskAlert(m.vehicle)}
                                {m.transportOrder?.individual && (
                                  <svg width="12" height="12" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg">
                                    <title>Los transport</title>
                                    <circle cx="6" cy="6" r="6" fill="green" />
                                  </svg>
                                )}
                                <span
                                  className="ms-3"
                                  title={
                                    'Van: ' +
                                    m.transportOrder?.addressFrom.addressLine1 +
                                    ' ' +
                                    m.transportOrder?.addressFrom.postcode +
                                    ' ' +
                                    m.transportOrder?.addressFrom.city
                                  }
                                >
                                  {m.transportOrder.vehicleManual ? (
                                    <>{m.transportOrder.vehicleManual || 'Geen titel'}</>
                                  ) : m.vehicle?.regplate ? (
                                    <>
                                      <RegplateLabel regplate={m.vehicle.regplate} width="80" />{' '}
                                      {m.vehicle?.brand_name || 'Unknown Brand'} {m.vehicle?.model_name || 'Unknown Model'}
                                    </>
                                  ) : (
                                    <>
                                      {m.vehicle?.vin?.slice(-6) || 'Unknown VIN'} {m.vehicle?.brand_name || 'Unknown Brand'}{' '}
                                      {m.vehicle?.model_name || 'Unknown Model'}
                                    </>
                                  )}
                                </span>
                              </div>
                              <div style={{ width: '20%' }}>
                                <span
                                  className={`badge ${
                                    m.vehicle?.outgoing_invoice_payment_status === 'PAID'
                                      ? 'bg-success'
                                      : m.vehicle?.outgoing_invoice_payment_status === 'PARTLY_PAID'
                                      ? 'bg-warning text-dark'
                                      : 'bg-danger'
                                  } me-2`}
                                >
                                  {m.vehicle?.outgoing_invoice_payment_status === 'PAID'
                                    ? 'Betaling'
                                    : m.vehicle?.outgoing_invoice_payment_status === 'PARTLY_PAID'
                                    ? 'Deels Betaald'
                                    : 'Betaling'}
                                </span>
                              </div>
                              <div
                                style={{
                                  width: '40%',
                                  color: m.transportOrder?.addressFrom?.addressLine1 !== 'Kleefstraat 8' ? 'red' : 'inherit',
                                }}
                              >
                                {'Naar: ' +
                                  m.transportOrder?.addressTo?.addressLine1 +
                                  ' ' +
                                  m.transportOrder?.addressTo?.postcode +
                                  ' ' +
                                  m.transportOrder?.addressTo?.city}
                              </div>
                            </div>
                          ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
}

export default MapWithMarkers;
